import React from "react";
import useList from "../..";
import { IListBulk, getT } from "./helperBulkActions";
import useCompleoReactHookForm, {
    useCompleoReactHookFormGetMainData
} from "customHooks/useCompleoReactHookForm";
import FormModal from "customHooks/useCompleoReactHookForm/components/FormModal";
import { useGetMetadataMerge } from "customHooks/useGetMetadata";
import useValuesFromSource from "customHooks/useValuesFromSource";
import { useTranslation } from "react-i18next";
import { apiDirectCall, ApiStateType } from "customHooks/useApi";
import Loading from "customHooks/useCompleoReactHookForm/helpers/Loading";
import { useAuthState } from "_ReactContext/AuthContext";
import { useQueryClient } from "react-query";
import useModalEvaluationList from "Pages/Applicant/ApplicantEvaluationRequest/useModalEvaluationList";
import { validateEmail } from "functions/util";
import { IValidateOnEntryDefinition } from "customHooks/useCompleoReactHookForm/Inputs/TagsCreatable";
import useJobContext from "Pages/Job/JobView/useJobContext";
import isEqual from "lodash/isEqual";
import { useModalAssessmentGroupList } from "Pages/Job/JobView/JobAssessments/AssessmentsGroupsList/helper";

interface IProps {
    formikRef: React.MutableRefObject<any>;
    listBulk: IListBulk[];
    setStepBulk: (value: React.SetStateAction<number>) => void;
    setLoadingButtons: React.Dispatch<React.SetStateAction<boolean>>;
}

const BulkActionsStep3RequestEvaluation = (props: IProps) => {
    const queryClient = useQueryClient();
    const { company } = useAuthState();
    const [data, dispatchData] = useJobContext();
    let defaultFieldsToHide: string[] = ["cvToShare"];
    const [fieldsToHide, setFieldsToHide] = React.useState<string[]>(
        defaultFieldsToHide
    );

    const { formikRef, listBulk, setStepBulk, setLoadingButtons } = props;

    const [listData] = useList();
    const customerFilterIdList: string[] = [];
    if (data.Job.data?.customer?.value) {
        customerFilterIdList.push(data.Job.data?.customer?.value);
    }

    // const { t } = listData.tempInfo;
    // const jobData = useJobList();
    const module1 = "ELASTICLISTSETTINGSBULKMERGE";
    const module2 = "ELASTICLISTSETTINGSBULKREQUESTEVALUATION";
    const [metadata] = useGetMetadataMerge(module1, module2);

    const [tLocal, i18n, readyTranslation] = useTranslation(
        [module2, module1],
        {
            useSuspense: false
        }
    );
    const tMerge = getT(tLocal, module1);
    const [lists, readyLists] = useModalEvaluationList({
        t: tMerge,
        readyTranslation: readyTranslation,
        customerFilterIdList: customerFilterIdList,
        jobId: data.JobId
    });
    const [
        listsAssessments,
        readyListsAssessments
    ] = useModalAssessmentGroupList({
        jobId: data.JobId,
        onlyWithTests: true,
        fieldName: "assessmentsTestsToShare",
        onlyLabelValue: true
    });

    const tagsAddProps: IValidateOnEntryDefinition = {
        errorMessage: tMerge("invalidEmail"),
        validateFunction: validateEmail
    };
    const addProps: Compleo.useCompleoForm.AdditionalPropertiesField[] = [
        {
            fieldName: "externalEvaluators",
            props: {
                validateOnEntryDefinition: tagsAddProps
            }
        }
    ];

    const [initialValues] = useValuesFromSource(metadata, true, {});
    const ready =
        readyTranslation &&
        metadata.status === "success" &&
        readyLists &&
        readyListsAssessments;

    const reactHookFormMethods = useCompleoReactHookFormGetMainData({
        t: tMerge,
        ready: ready,
        i18nV: i18n,
        metadadosRetorno: metadata,
        valuesFromSource: initialValues
    });
    const { watch } = reactHookFormMethods.reactHookFormMethods;
    const displayCVField = watch("displayCV");
    React.useEffect(() => {
        let fieldsToHideFN = defaultFieldsToHide;
        if (displayCVField) {
            fieldsToHideFN = defaultFieldsToHide.filter(
                (item) => item !== "cvToShare"
            );
        }
        if (!isEqual(fieldsToHide, fieldsToHideFN)) {
            setFieldsToHide(fieldsToHideFN);
        }
    }, [displayCVField]);

    const handleSubmit = async (values: any) => {
        // const type = values.filterType.value;
        setLoadingButtons(true);
        const ids = listBulk
            .filter((item) => item.checked)
            .map((item) => item.value);
        try {
            const paramsPost: Compleo.IObject = {
                companyId: company.companyId,
                ids: ids,
                bulkSelectionId: listBulk[0]?.bulkSelectionId,
                bulkProtocolVersion: 1,
                ...values,
                type: "applicantjob_requestEvaluation",
                job: {
                    value: data.JobId
                }
            };
            const dataAPI = await apiDirectCall(
                "/bulkactions/baadd",
                "post",
                paramsPost
            );
        } catch (ex) {
            setLoadingButtons(false);
            return ex.response;
        }
        // listData.tempInfo.useApiKey
        queryClient.invalidateQueries(listData.tempInfo.useApiKey);
        setLoadingButtons(false);
        setStepBulk(99);
        const retorno: Compleo.IObject = {};
        retorno.status = 200;
        return retorno;
    };
    const postReturn: ApiStateType = {
        status: "success",
        response: {},
        exception: null
    };

    const [formCompleo, finished] = useCompleoReactHookForm({
        t: tMerge,
        ready: ready,
        i18nV: i18n,
        postMethod: handleSubmit,
        postReturn: postReturn,
        FormType: FormModal,
        metadadosRetorno: metadata,
        valuesFromSource: initialValues,
        formGroupPaperElevation: 0,
        formRef: formikRef,
        customLists: [...lists, ...listsAssessments],
        reactHookFormMethods: reactHookFormMethods,
        additionalFieldProperties: addProps,
        fieldsToHide
    });

    let FormReturn: JSX.Element = <Loading />;
    if (finished && ready) {
        FormReturn = formCompleo;
    }

    return FormReturn;
};

export default BulkActionsStep3RequestEvaluation;
